import React from 'react';
import { Text, View } from 'react-native';
import i18n from 'i18n-js';
import moment from 'moment';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { WithColor } from '@styles/colors';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { modalFieldBg } from '@v2/pages/common/constants/base.constants';
import { SmallLoading } from '@components/loading';
import { generateImageURL as generateImageURLAction } from '@reducers/blobStorage';
import {
  GlobalSearchItem,
  ProductionOrderSearchItem,
} from '../../../../interfaces/search';

import BlockHeader from './BlockHeader';
import LineProgressButton from './LineProgressButton';
import OperationsList from './OperationsList';
import { useManageProductionOrderBlock } from '@components/productionInformation/baseWithERP/hooks/useManageProductionOrderBlock';
import { SelectedLevel } from '@components/productionInformation/baseWithERP/types';

export interface ProductionOrderBlockProps extends WithColor {
  ncFileName?: string;
  item: GlobalSearchItem | null;
  existingProductionOrder?: ProductionOrderSearchItem;
  existingPart?: GlobalSearchItem | null;
  hideMoveHistoryBackButton?: boolean;
  setHistoryPool: any;
  setSelectedLevel: any;
  handleLink: (args: {
    partId?: number;
    productionOrderId?: number;
    productionOperationId?: number;
    recipeId?: number;
    recipeOperationId?: number;
  }) => void;
  scale?: ScaleFunction;
  font?: typeof initialFont;
}

const ProductionOrderBlock = ({
  C,
  ncFileName,
  item,
  existingProductionOrder,
  existingPart,
  hideMoveHistoryBackButton,
  setHistoryPool,
  setSelectedLevel,
  handleLink,
  scale = initialScale,
  font = initialFont,
}: ProductionOrderBlockProps) => {
  const dispatch = useAppDispatch();
  const generateImageURL = (data: Parameters<typeof generateImageURLAction>[0]) => dispatch(generateImageURLAction(data));

  const { productionOrder, setProductionOrder, part, operations, isLoading } = useManageProductionOrderBlock({
    item,
    existingProductionOrder,
    existingPart,
  });

  if (isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center' }}>
        <SmallLoading />
      </View>
    );
  }

  return (
    <>
      <BlockHeader
        {...{
          scale,
          font,
          C,
          documentData: existingPart ? existingPart?.documentData : item?.documentData,
          itemDescription: existingPart ? existingPart?.itemDescription : part?.partName,
          itemMemo: existingPart ? existingPart?.itemMemo : part?.description,
          itemType: 'Part',
          generateImageURL,
          hideMoveHistoryBackButton,
          onDeletePress: () => {
            setHistoryPool([{ resetPart: true, resetLevelTo: null }]);
            setSelectedLevel(null);
            setProductionOrder(undefined);
          },
        }}
      />
      <View style={{ marginBottom: scale(31), paddingHorizontal: scale(18) }}>
        <Text numberOfLines={1} style={[font['Title 2'], { color: C('Label/Light/Secondary'), marginBottom: scale(10) }]}>
          {i18n.t('Production Order')}
        </Text>
        <LineProgressButton
          {...{
            scale,
            font,
            C,
            progress: productionOrder?.percentageComplete,
            bg: modalFieldBg,
            height: 66,
            title: productionOrder?.productionOrderNumber,
            text: productionOrder?.scheduledEndDate && `${moment(productionOrder.scheduledEndDate).format('D MMM YYYY')}`,
            onAdditionalBtnPress: () => {
              setHistoryPool([{ resetPart: true, resetLevelTo: null }]);
              setSelectedLevel(SelectedLevel.Part);
              setProductionOrder(undefined);
            },
            additionalBtnIcon: 'xMark',
            additionalBtnColor: 'Label/Light/Primary',
            status: productionOrder?.isActive ? 'running' : 'completed',
            shadow: false,
          }}
        />
      </View>
      <View style={{ flex: 1 }}>
        <Text
          numberOfLines={1}
          style={[font['Title 2'], { color: C('Label/Light/Primary'), marginBottom: scale(10), minHeight: scale(25), paddingLeft: scale(18) }]}
        >
          {i18n.t('Select Operation')}
        </Text>
        <OperationsList
          {...{
            C,
            scale,
            font,
            operations,
            ncFileName,
            keyExtractor: (op) => String(op.productionOperationId),
            onLinkPress: (op) =>
              handleLink({
                productionOperationId: op.productionOperationId,
                productionOrderId: productionOrder?.productionOrderId,
                partId: productionOrder?.partId,
              }),
          }}
        />
      </View>
    </>
  );
};

export default ProductionOrderBlock;
